import React, { useMemo, useState } from 'react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { ShieldCheck, Layers3 } from 'lucide-react';
import { useRBAC } from '../../lib/RBACContext';

function deriveScope(roles = [], recordAccess = 'own') {
  const roleSet = new Set(roles);
  if (roleSet.has('admin') || roleSet.has('system_admin') || roleSet.has('sales_admin')) {
    return {
      label: 'All Data',
      tone: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      description: 'You are viewing company-wide data, further narrowed only by active filters.',
      bullets: ['Scope includes all available records for your organization.', 'Use page filters to narrow the view to a specific rep, account, or time period.'],
    };
  }
  if (roleSet.has('sales_director') || roleSet.has('finance_manager') || roleSet.has('marketing_manager')) {
    return {
      label: 'Department Data',
      tone: 'bg-blue-100 text-blue-700 border-blue-200',
      description: 'You are viewing department/team-level data according to your role visibility and hierarchy.',
      bullets: ['Scope includes your allowed department-level records and subordinates where applicable.', 'Use filters to focus on a specific segment inside your permitted scope.'],
    };
  }
  if (roleSet.has('product_director') || roleSet.has('product_manager')) {
    return {
      label: 'Product Scope',
      tone: 'bg-violet-100 text-violet-700 border-violet-200',
      description: 'You are viewing opportunities, orders, and analytics relevant to your product / product-manager scope.',
      bullets: ['Scope is filtered to your mapped product manager or product-related data.', 'If something looks missing, verify the product mapping or active filters first.'],
    };
  }
  if (recordAccess === 'own' || roleSet.has('sales_rep') || roleSet.has('user')) {
    return {
      label: 'My Data',
      tone: 'bg-slate-100 text-slate-700 border-slate-200',
      description: 'You are viewing only your own records or records assigned to you.',
      bullets: ['Counts and KPIs reflect your own accessible data only.', 'Use filters to narrow further, but they cannot expand beyond your allowed visibility.'],
    };
  }
  return {
    label: 'Role Data',
    tone: 'bg-slate-100 text-slate-700 border-slate-200',
    description: 'This page reflects the data scope allowed by your role and access rules.',
    bullets: ['Scope depends on your assigned roles and RBAC configuration.'],
  };
}

export function RoleScopeControl({ dataTestId = 'role-scope-control' }) {
  const [open, setOpen] = useState(false);
  const { roles = [], recordAccess = 'own' } = useRBAC();
  const scope = useMemo(() => deriveScope(roles, recordAccess), [roles, recordAccess]);

  return (
    <>
      <div className="flex items-center gap-2" data-testid={dataTestId}>
        <Badge variant="outline" className={`${scope.tone} text-[10px]`}> <Layers3 className="h-3 w-3 mr-1" /> {scope.label}</Badge>
        <Button variant="outline" size="sm" className="h-8 rounded-full px-3 text-[11px]" onClick={() => setOpen(true)} data-testid={`${dataTestId}-open-button`}>
          <ShieldCheck className="h-3.5 w-3.5 mr-1.5" /> Scope
        </Button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg" data-testid={`${dataTestId}-dialog`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[#800000]" /> Data Scope</DialogTitle>
            <DialogDescription>{scope.description}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Badge variant="outline" className={`${scope.tone} text-xs`}>{scope.label}</Badge>
            <ul className="space-y-2">
              {scope.bullets.map((bullet) => (
                <li key={bullet} className="text-sm text-slate-700 flex gap-2">
                  <span className="text-[#800000]">•</span>
                  <span>{bullet}</span>
                </li>
              ))}
            </ul>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
